/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { Capacitor } from '@capacitor/core';
import {
  calculateBearing,
  calculateDistanceBetweenTwoPointsSphericalLawOfCosines,
  deepEqual,
} from '@deckee/utilities';
import { throttle } from 'throttle-debounce';
import classNames from 'classnames/bind';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { matchPath } from 'react-router-dom';
import { callEndpoint, telemetry } from '../../../../../services/backend';
import { captureEvent } from '../../../../../services/event-capture';
import { activateFreeMapMode } from '../../../../../services/log-trips';
import {
  getLayerDefinitions,
  layerExists,
} from '../../../../../services/map-layer';
import { definitions, emit } from '../../../../../services/message-broker';
import { MAP_URL } from '../../../../../services/route/permissions';
import {
  ROUTE_DROP_PIN,
  ROUTE_MAP,
  ROUTE_MARKER_SUMMARY_DETAILS,
  ROUTE_REGULATIONS,
  ROUTE_SUPPORT,
  ROUTE_TRIP_ACTIVE,
} from '../../../../../services/route/route-names';
import { transitionToNextRoute } from '../../../../../services/route/route-navigation';
import { selectHasCurrentMemberActiveSubscription } from '../../../../../services/store/modules/backend-service';
import usePaywall from '../../../../hooks/use-paywall';
import PopOverMessage from '../../../../shared/PopOverMessage/PopOverMessage';
import {
  defaultMapMarker,
  iconPin,
  imgElementSvg,
  stringSvg,
} from '../../../../shared/vector-images';
import AisVesselDetails from '../../../../shared/_deprecated/components/AisVesselDetails/AisVesselDetails';
import MarkerSummary from '../../../../features/Markers/MarkerSummary/MarkerSummary';
import './raw.css';
import styles from './view.scss';

const map3dPitch =
  window.DECKEE_CONFIG && parseInt(window.DECKEE_CONFIG.map3dPitch);
const map3dExaggeration =
  window.DECKEE_CONFIG && parseInt(window.DECKEE_CONFIG.map3dExaggeration);
const mapRangeStart =
  window.DECKEE_CONFIG && parseInt(window.DECKEE_CONFIG.mapRangeStart);
const mapRangeEndDistance =
  window.DECKEE_CONFIG && parseInt(window.DECKEE_CONFIG.mapRangeEndDistance);
const map3dTerrainTileSize =
  window.DECKEE_CONFIG && parseInt(window.DECKEE_CONFIG.map3dTerrainTileSize);
const mapFlyToDuration =
  window.DECKEE_CONFIG && parseInt(window.DECKEE_CONFIG.mapFlyToDuration);

const cx = classNames.bind(styles);

const wrapBounds = (bounds) => ({
  _sw: bounds.getSouthWest().wrap(),
  _ne: bounds.getNorthEast().wrap(),
});

/**
 * Extract the event from Mapbox to get the clicked location
 * @param {object} event - Mapbox event to extract data from
 * @return {object} latitude longitude of event, returns null if not available
 */
const extractLatitudeLongitudeFromEvent = ({ event }) => ({
  clickedLatitude: event?.lngLat?.lat ?? null,
  clickedLongitude: event?.lngLat?.lng ?? null,
});

const captureMapboxEvent = async ({ event, type, payload }) => {
  await captureEvent({
    type,
    payload: {
      ...payload,
      ...extractLatitudeLongitudeFromEvent({ event }),
    },
  });
};

// The time window for a double or long press
const ADVANCED_PRESS_TIME = 600;

// This is the catch time for duplicate events. This is solely to cater for duplicate events from mapbox.
// Some anecdotal  evidence pointed to double clicks with a mouse < 100ms were difficult. So we accept
// there is a window where a click may be missed
const PRESS_EVENT_THROTTLE_TIME = 10;

class MapBox extends React.Component {
  // Flag indicating the map moved between starting a press, and stopping a press
  didMove = false;

  // Indicate a long press was activated before the stop press callback
  didLongPress = false;

  // Indicates there was a double press within the stop press callback
  didDoublePress = false;

  // Gets executed after a time limit for long press and canceled in other scenarios
  longPressTimer = null;

  // A timer that waits to see if a double click or long press is activated
  singlePressWaitTimer = null;

  // Setup event handlers complete
  setupEventHandlersComplete = false;

  // For calculating double taps on mobile
  lastClickTime = 0;

  componentDidMount() {
    mapboxgl.accessToken =
      'pk.eyJ1IjoibWlrZWJvYXRhIiwiYSI6ImNqODI4MzB0bDUyMnEyd28zZzhnbHlnNjUifQ.D9Pxal6hLEnREf4HsYrSdQ';

    const {
      mapControlInitialize,
      baseStyle,
      currentPosition: startPosition,
      layerDependencies,
    } = this.props;

    // This is gross, but there is no established pattern to get the map outside of a layer. In addition, the map can re-render when this component remounts
    window.deckeeMap = this.map = new mapboxgl.Map({
      container: this.mapContainer,
      style: baseStyle,
      center: [startPosition.lon, startPosition.lat],
      zoom: 10,
      logoPosition: 'bottom-left',
      hash: true,
      attributionControl: true,
      customAttribution: '© Deckee and other data providers',
      minZoom: 2,
      maxZoom: 20,
      renderWorldCopies: true,
      doubleClickZoom: { around: 'center' },
      touchZoom: { around: 'center' },
    });

    this.clearSinglePressWaitTimer = () => {
      if (this.singlePressWaitTimer) {
        clearTimeout(this.singlePressWaitTimer);
      }
    };

    this.ifNotDoubleClick = (callback) => {
      this.clearSinglePressWaitTimer();
      this.singlePressWaitTimer = setTimeout(callback, ADVANCED_PRESS_TIME);
    };

    // This is just a utility function enable logging of key events. Seperated from telemetry as they are very noisy, and
    // is used in a very specific debugging circumstance
    this.debugMapEvent = (name, ...rest) => {
      if (false) {
        console.log(
          'DEBUG MAP EVENT',
          name,
          ...rest,
          this.didMove,
          this.didDoublePress,
          this.didLongPress,
        );
      }
    };

    // Keep track of the registered event handlers. Currently not used except for debugging. But we should consider instead of
    // not re-registering event handlers, we should remove and then re-add them. This will allow us to update the event handlers
    const registeredHandlers = [];

    // A helper wrapper for mapbox events, mainly used so we can debug by adding logs
    this.onMap = (name, fn, ...rest) => {
      const handlerFunction = (...x) => fn(...x);
      registeredHandlers.push({ name, handlerFunction });
      this.map.on(name, handlerFunction, ...rest);
    };

    mapControlInitialize(this.map);

    this.getCurrentPath = () => {
      // Some ios devices report an incorrect pathname, I think this is just using the initial URL.
      // Attempt to use the react router history pathname, and fallback to the window pathname
      try {
        // eslint-disable-next-line react/destructuring-assignment
        return this.props.history.location.pathname;
      } catch (e) {
        telemetry(telemetry.WARN, '[mapbox] cannot use react-router pathname');
        return window.location.pathname;
      }
    };

    this.isMeasureDistance = () =>
      matchPath(this.getCurrentPath(), {
        path: ['/maps/distance'],
        exact: false,
      });

    this.disabledMapClick = () =>
      matchPath(this.getCurrentPath(), {
        path: ['/maps/plan/details/choose-location'],
        exact: false,
      });

    this.safeToDropPin = () => {
      const isSafe = matchPath(this.getCurrentPath(), {
        path: ['/maps', '/maps/drop-pin'],
        exact: true,
      });

      return isSafe;
    };

    this.transitionToDropPin = ({ latitude, longitude }) => {
      this.debugMapEvent('TRANSITION TO DROP PIN');

      if (this.safeToDropPin()) {
        const { dispatchClosePopover, dispatchSetDropPinLocation } = this.props;
        dispatchClosePopover();
        dispatchSetDropPinLocation({
          latitude,
          longitude,
        });
        transitionToNextRoute(ROUTE_DROP_PIN);
      }
    };

    this.clearLongPressTimer = () => {
      if (this.longPressTimer != null) {
        clearTimeout(this.longPressTimer);
        this.longPressTimer = null;
      }
    };

    // Activate a long press once we've deviced there has been one
    this.activateLongPress = async (event) => {
      this.debugMapEvent('ACTIVATE LONG PRESS');
      this.transitionToDropPin({
        latitude: event.lngLat.lat,
        longitude: event.lngLat.lng,
      });
    };

    // This function is to execute code AFTER we've decided there has been a single press (tap or click).
    // This abstracts from the complexity of long press/double tap/single tap events from mapbox
    this.activateSinglePress = async (event) => {
      this.debugMapEvent('ACTIVATE SINGLE PRESS', event);
      const isSafeToDropPin = this.safeToDropPin();
      const isMeasuringDistance = this.isMeasureDistance();
      const { dispatchShowPopover, dispatchUpdateSelectedMarker } = this.props;
      let ignoredFeature = null;
      const boundingBox = [
        [event.point.x - 1, event.point.y - 1],
        [event.point.x + 1, event.point.y + 1],
      ];
      const features = this.map.queryRenderedFeatures(boundingBox);

      try {
        captureMapboxEvent({
          type: 'map: click',
          event,
          payload: {
            numberOfFeatures: features.length,
          },
        });
        // eslint-disable-next-line no-empty
      } catch (e) {}

      // List of sources that, if included in the features list, shouldn't have a pin dropped
      // (likely click is handled elsewhere)
      const IGNORED_SOURCE_NAMES = [
        'signalPositions',
        'storyMarker',
        'distanceToolLine',
        'distanceToolPoint',
      ];

      // If the feature is for distance tool, or the route is for distance tool, then just ignore everything
      // NOTE: the check for source is probably not necessary, but was existing logic
      if (
        isMeasuringDistance ||
        features.find(
          (feature) =>
            feature.source &&
            ['distanceToolLine', 'distanceToolPoint'].indexOf(feature.source) >
              -1,
        )
      ) {
        return;
      }

      if (this.disabledMapClick()) {
        return;
      }

      dispatchUpdateSelectedMarker({});
      if (features && features.length) {
        // Reset the popover and route
        this.resetPopoverState();

        let aisFeature = null;
        let urlFeature = null;

        // NOTE: the order here is important

        // These layer types have an in-app url associated to them, so lets just clear any existing popover then
        // let the applications routing take care of the rest
        if (
          features.find((feature) =>
            [
              'standardMarkerLayer',
              'signalPositions',
              'eventMarker',
              'boatUsMarker',
              'partnerMarker',
            ].includes(feature?.source),
          )
        ) {
          // map marker found - we just load the popover and the route will load the content
          dispatchShowPopover('');
        } else if (
          (aisFeature = features.find(
            (feature) => feature.source && feature.source === 'ais',
          ))
        ) {
          // aisFeature found - display the AIS popover
          await this.displayAisFeaturePopover(event, aisFeature);
        } else if (
          (urlFeature = features.find(
            (feature) => feature.properties && feature.properties.url,
          ))
        ) {
          // urlFeature found - display the url feature popover
          await this.displayUrlFeaturePopover(event, urlFeature);
        } else if (
          features.find(
            (feature) =>
              feature.layer &&
              feature.layer.id &&
              feature.layer.id.includes('activity-heatmap'),
          )
        ) {
          captureMapboxEvent({
            type: 'heatmap: click',
            event,
            payload: {},
          });
          const buttons = [
            {
              buttonAction: this.props.openSupportOrPresentPaywall,
              buttonLabel: 'Message support',
            },
            {
              buttonAction: () =>
                transitionToNextRoute(ROUTE_REGULATIONS, {
                  latitude: event.lngLat.lat,
                  longitude: event.lngLat.lng,
                }),
              buttonLabel: 'Regulations',
            },
          ];

          if (Capacitor.isNativePlatform()) {
            buttons.push({
              buttonAction: () => transitionToNextRoute(ROUTE_TRIP_ACTIVE),
              buttonLabel: 'Contribute',
            });
          }
          dispatchShowPopover(
            <PopOverMessage
              details="Discover routes and new places to explore, based on aggregated community activity."
              buttons={buttons}
            />,
            'Heatmaps',
          );
        } else if (
          (ignoredFeature = features.find(
            (feature) =>
              feature.source &&
              IGNORED_SOURCE_NAMES.indexOf(feature.source) > -1,
          ))
        ) {
          this.debugMapEvent(
            `Map feature has been ignored by drop pin`,
            ignoredFeature.source,
          );
        } else if (isSafeToDropPin) {
          this.ifNotDoubleClick(() =>
            this.transitionToDropPin({
              latitude: event.lngLat.lat,
              longitude: event.lngLat.lng,
            }),
          );
        }

        // This serves to add the 'zoom/lat/lon' hash to the url when changing routes
        if (this.mapControl) this.mapControl.easeTo({});
      } else if (isSafeToDropPin) {
        this.ifNotDoubleClick(() =>
          this.transitionToDropPin({
            latitude: event.lngLat.lat,
            longitude: event.lngLat.lng,
          }),
        );
      }

      this.map.fire('deckee-press', event);
    };

    this.displayAisFeaturePopover = async (event, aisFeature) => {
      const {
        dispatchClearFollowModeActive,
        dispatchMoveMapToLocation,
        dispatchShowPopover,
      } = this.props;

      dispatchShowPopover(''); // add the popover load state
      dispatchClearFollowModeActive();

      await captureMapboxEvent({
        type: 'ais: click',
        payload: {
          aisType: aisFeature?.properties?.type,
          aisShipName: aisFeature?.properties?.shipName,
          aisMMSI: aisFeature?.properties?.mmsi,
          aisIcon: aisFeature?.properties?.icon,
        },
        event,
      });

      const { icon, shipName, type, speed, timestamp } = aisFeature.properties;
      const title = shipName
        .toLowerCase()
        .replace(/(^| )(\w)/g, (s) => s.toUpperCase());

      dispatchShowPopover(
        <AisVesselDetails
          icon={icon}
          category={type}
          speed={speed}
          lastUpdated={moment(timestamp).fromNow()}
        />,
        title,
      );

      await dispatchMoveMapToLocation({
        center: {
          lat: event.lngLat.lat,
          lon: event.lngLat.lng,
        },
      });
    };

    this.displayUrlFeaturePopover = async (event, urlFeature) => {
      const {
        dispatchClearFollowModeActive,
        dispatchMoveMapToLocation,
        dispatchShowPopover,
      } = this.props;

      dispatchShowPopover(''); // add the popover load state
      dispatchClearFollowModeActive();

      // Pull the api data for this feature
      const { url } = urlFeature.properties;

      await captureMapboxEvent({
        type: 'url: feature-click',
        payload: {
          urlFeatureIconToShow: urlFeature?.properties?.icontoshow,
          urlFeatureTextToDisplay: urlFeature?.properties?.texttodisplay,
          urlFeatureURL: urlFeature?.properties?.url,
          // eslint-disable-next-line camelcase
          urlFeatureIntegrationID: urlFeature?.properties?.integration_id,
          urlFeatureSource: urlFeature?.source,
          urlFeatureSourceLayer: urlFeature?.sourceLayer,
        },
        event,
      });

      const data = await callEndpoint(
        callEndpoint.endpoints.getRegulationById,
        {
          ':id': encodeURIComponent(url),
        },
      );

      const coordinatesCentroid = data.centroid;
      const clickedLatLon = {
        lat: event.lngLat.lat,
        lon: event.lngLat.lng,
      };

      // Check for a popover button override
      let popoverButton = null;
      let buttonLabel = null;
      let buttonHref = null;
      let buttonAction;

      if (data && data.popover && data.popover.buttonOverrides) {
        popoverButton = data.popover.buttonOverrides.find(
          (override) => override.type === 'popoverButtonOverride',
        );
      }

      if (popoverButton) {
        buttonLabel = popoverButton.label;

        // Setup the button href or action
        if (popoverButton.url) {
          if (
            popoverButton.url.indexOf('http://') !== 0 ||
            popoverButton.url.indexOf('https://') !== 0
          ) {
            buttonHref = `http://${popoverButton.url}`;
          } else {
            buttonHref = popoverButton.url;
          }
        }
        if (!popoverButton.url && data) {
          buttonAction = () =>
            transitionToNextRoute(ROUTE_MARKER_SUMMARY_DETAILS, {
              url: encodeURIComponent(url),
            });
        }
      }

      // Check if we have the required data to display the popover
      if (data && data.popover && data.popover.title) {
        dispatchShowPopover(
          <MarkerSummary
            latLon={{
              lat: +coordinatesCentroid.coordinates[1],
              lon: +coordinatesCentroid.coordinates[0],
            }}
            title={data.popover.title}
            description={data.popover.description}
            customDescription={data.popover.customDescription}
            banner={data.popover.banner}
            buttonAction={buttonAction}
            buttonLabel={buttonLabel}
            buttonHref={buttonHref}
          />,
          data.popover.title,
        );
        // Move the map center to the marker location
        await dispatchMoveMapToLocation({
          center: clickedLatLon,
        });

        window.history.replaceState({}, '', `${MAP_URL}`);
      }
    };

    /**
     * SINGLE PRESS, DOUBLE PRESS AND LONG PRESS THEORY
     *
     * The idea is that on a keydown event (touchstart, mousedown) then we should reset the press state. Then on
     * keyup (mouseup, touchend) it should compare the new state and determine what type of press was activated.
     * The long press is the only press that behaves different, as it will be executed before the keyup if
     * appropriate
     */
    this.addLayers = () => {
      const { dispatchClosePopover } = this.props;

      const layerDefinitions = getLayerDefinitions();
      const layerPromises = Object.keys(layerDefinitions).map(async (layer) => {
        const definition = layerDefinitions[layer];
        if (layerDefinitions[layer].onInitialize) {
          await definition.onInitialize(this.map, layerDependencies);
        }
      });

      // Double clicks are handled by mapbox - this is just to keep track of the period when a double click could occur
      const executeDoubleClick = () => {
        this.debugMapEvent('DOUBLE CLICK');
        // Clears any existing single press's that are waiting
        this.clearSinglePressWaitTimer();
        dispatchClosePopover();
      };

      // Create a timer for a long press. This timer can be cleared from anywhere in this class
      const beginLongPressWait = async (event) => {
        this.clearLongPressTimer();

        this.longPressTimer = setTimeout(() => {
          this.didLongPress = true;
          if (this.longPressTimer) {
            this.activateLongPress(event);
          }
        }, ADVANCED_PRESS_TIME);
      };

      const setupMapEventHandlers = () => {
        if (this.setupEventHandlersComplete) {
          return;
        }
        // NOTE: We need to throttle these event methods as on some device mapbox triggers both mouse and touch events
        // It is important that these methods aren't called multiple times for a single "press"
        const startPress = throttle(
          PRESS_EVENT_THROTTLE_TIME,
          (event) => {
            this.debugMapEvent('START PRESS', event);
            // If mapbox is reporting the map is moving, then we should use that. Otherwise being the press start, we
            // can assume that it's not moving.
            this.didMove = this.map.isMoving() || false;

            // Reset double press on every start press, it is determined by time
            this.didDoublePress = false;

            // Record time between last click
            const currentTime = new Date().getTime();
            const timeBetweenTaps = currentTime - this.lastClickTime;
            this.lastClickTime = currentTime;

            // If it wasn't moving on the press, then start the timer. The key here is that on mousedown or touch down mapbox
            // reports the map as still moving, however for mouseup and touchup the map has stopped moving already
            if (!this.didMove) {
              beginLongPressWait(event);

              // Check the double press threshhold, if it's within the time then treat it as a double press.
              if (timeBetweenTaps < ADVANCED_PRESS_TIME) {
                // NOTE: double press gets registered even when you double press when "throwing" the map
                this.didDoublePress = true;
                this.clearSinglePressWaitTimer();
                this.clearLongPressTimer();
                executeDoubleClick();
              }
            }
          },
          { noTrailing: true },
        );

        const stopPress = throttle(
          PRESS_EVENT_THROTTLE_TIME,
          (event) => {
            this.debugMapEvent('STOP PRESS', event);
            // A long press is the only event that is determined on keydown alone. Everything else should run through the
            // `stopPress`, which is essentially the keyup. This means by the time this runs, the long press has already
            // been executed, or didn't happen in the first place
            this.didLongPress = false;
            this.clearLongPressTimer();
            this.clearSinglePressWaitTimer();

            // If `didMove` is true, then it means the user has moved the cursor position from it's startPress location
            if (this.didMove) {
              return;
            }

            if (!this.didDoublePress) {
              this.activateSinglePress(event);
            }
          },
          { noTrailing: true },
        );

        this.onMap('touchstart', startPress);
        this.onMap('touchend', stopPress);

        if (!Capacitor.isNativePlatform()) {
          this.onMap('mousedown', startPress);
          this.onMap('mouseup', stopPress);
        }

        this.onMap('move', () => {
          this.didMove = true;
          this.clearLongPressTimer();
        });

        // dblclick only reliably works on desktop, mobile double click is handled in touchstart
        this.onMap('dblclick', executeDoubleClick);

        this.setupEventHandlersComplete = true;
      };

      setupMapEventHandlers();

      Promise.all(layerPromises).then(() => {
        this.map.easeTo({});
      });
    };

    // Notify the parent component of an update to the viewport. We need to make the parent use
    // messages instead.
    this.emitViewPortUpdatedIfRequired = (() => {
      let prevBounds;
      let nextBounds;
      return (mapEvent) => {
        const { onViewPortUpdated } = this.props;
        const givenBounds = wrapBounds(mapEvent.target.getBounds());

        // Only emit the viewPortUpdate if the bounds of the map have changed. This event is only
        // concerned with movement of the map.
        nextBounds = JSON.stringify(givenBounds);
        if (prevBounds === nextBounds) return;
        prevBounds = nextBounds;

        // Transform mapBox formatted data to our own format, namely using lon not lng.

        /* eslint-disable no-underscore-dangle */
        const ne = givenBounds._ne;
        const sw = givenBounds._sw;
        /* eslint-enable no-underscore-dangle */
        const bounds = [
          { lat: ne.lat, lon: sw.lng },
          { lat: sw.lat, lon: ne.lng },
        ];

        const center = {
          lat: +mapEvent.target.getCenter().lat,
          lon: +mapEvent.target.getCenter().lng,
        };

        const nextViewPort = {
          center,
          bounds,
          zoom: mapEvent.target.getZoom(),
          bearing: mapEvent.target.getBearing(),
          pitch: mapEvent.target.getPitch(),
        };
        if (onViewPortUpdated) {
          onViewPortUpdated(nextViewPort);
        }
        emit(definitions.map.viewPortUpdated, nextViewPort);
      };
    })();

    // Detect if user moved map when follow mode active
    // - and disable follow mode
    this.onMap('touchend', () => {
      const { followModeActive } = this.props;

      if (followModeActive) {
        activateFreeMapMode();
      }
    });

    this.onMap('moveend', (event) => {
      // Debounced late-worker pattern. Abstract this.
      const later = () => {
        this.moveendTimeout = null;
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        worker();
      };
      clearTimeout(this.moveendTimeout);
      this.moveendTimeout = setTimeout(later, 200);

      const worker = async () => {
        this.emitViewPortUpdatedIfRequired(event);
        const layerDefinitions = getLayerDefinitions();
        Object.keys(layerDefinitions).forEach((layer) => {
          // eslint-disable-next-line no-warning-comments
          // TODO: Should we be filtering on if layers are shown or not here??
          // This change will require a bit of testing so this should be a task to check at some
          // stage in the future.
          if (layerDefinitions[layer].onMapUpdateEvent) {
            layerDefinitions[layer].onMapUpdateEvent(
              this.map,
              layerDependencies,
            );
          }
        });
      };
    });

    this.onMapLoad = () => {
      const startMap = () => {
        if (!this.mapStarting) {
          this.mapStarting = true;
          this.addLayers();

          // Now we've added everything, set mapInitiated so that other functionality knows it can
          // edit layers and modify the map as needed.
          this.mapInitiated = true;
          this.updateFromPropsWhereRequired();
        }
      };
      const waiting = () => {
        if (!this.map.isStyleLoaded()) {
          setTimeout(waiting, 200);
        } else {
          startMap();
        }
      };
      waiting();

      if (!this.map.getSource('terrain')) {
        this.map.setFog({
          range: [mapRangeStart, mapRangeEndDistance],
          color: 'rgb(186, 210, 235)', // Lower atmosphere
          'high-color': '#add8e6',
          'horizon-blend': 0.02, // Atmosphere thickness (default 0.2 at low zooms)
          'space-color': '#d8f2ff', // Background color
        });

        this.map.addSource('terrain', {
          type: 'raster-dem',
          url: 'mapbox://mapbox.mapbox-terrain-dem-v1',
          tileSize: map3dTerrainTileSize,
        });
      }
    };

    // 'style.load' event is deprecated. Recommendation is to use 'styledata'
    // but there are reports with this not working too. Work around is to use 'idle'
    // https://github.com/mapbox/mapbox-gl-js/issues/3970
    // https://github.com/mapbox/mapbox-gl-js/issues/8765
    // https://github.com/alex3165/react-mapbox-gl/pull/171
    // this.onMap('idle', this.onMapLoad);
    this.onMap('styledata', this.onMapLoad);

    // this.onMap('styledata', async (event) => {
    //   // Debounced late-worker pattern. Abstract this.
    //   const later = () => {
    //     this.styledataTimeout = null;
    //     /* eslint-disable-next-line no-use-before-define */
    //     worker();
    //   };
    //   clearTimeout(this.styledataTimeout);
    //   this.styledataTimeout = setTimeout(later, 200);

    //   const worker = async () => {
    //     this.emitViewPortUpdatedIfRequired(event);
    //   };
    // });

    this.onMap('load', (event) => {
      // Fire this as when we're working on desktop, the map is sometimes sized wrong after load
      this.map.resize();
      this.emitViewPortUpdatedIfRequired(event);
    });

    this.distanceToolOnMove = (event) => {
      this.clearLongPressTimer();
      const { lat, lng: lon } = event.lngLat;
      if (this.pointsData && this.lineData && this.map) {
        this.pointsData.features[1].geometry.coordinates = [lon, lat];
        this.lineData.features[0].geometry.coordinates[1] = [lon, lat];
        this.map.getSource('distanceToolPoints')?.setData(this.pointsData);
        this.map.getSource('distanceToolLine')?.setData(this.lineData);
        this.displayToolMarker.setLngLat([lon, lat]);
        const distance = calculateDistanceBetweenTwoPointsSphericalLawOfCosines(
          this.pointsData.features[0].geometry.coordinates[1],
          this.pointsData.features[0].geometry.coordinates[0],
          this.pointsData.features[1].geometry.coordinates[1],
          this.pointsData.features[1].geometry.coordinates[0],
          'nm',
        );
        this.displayToolMarker
          .getElement()
          .querySelector('#distance').innerHTML = distance.toFixed(
          distance > 100 ? 0 : 1,
        );
        this.displayToolMarker
          .getElement()
          .querySelector('#heading').innerHTML = calculateBearing(
          this.pointsData.features[0].geometry.coordinates[1],
          this.pointsData.features[0].geometry.coordinates[0],
          this.pointsData.features[1].geometry.coordinates[1],
          this.pointsData.features[1].geometry.coordinates[0],
        ).toFixed(0);
      }
    };

    this.distanceToolOnMoveEnd = (/* event */) => {
      this.map.off('mousemove', this.distanceToolOnMove);
      this.map.off('touchmove', this.distanceToolOnMove);
    };

    this.distanceToolSwapDirection = () => {
      if (this.pointsData && this.lineData) {
        this.pointsData.features = [
          this.pointsData.features[1],
          this.pointsData.features[0],
        ];
        this.pointsData.features[0].properties.itemIndex = 0;
        this.pointsData.features[1].properties.itemIndex = 1;

        this.lineData.features[0].geometry.coordinates = [
          this.lineData.features[0].geometry.coordinates[1],
          this.lineData.features[0].geometry.coordinates[0],
        ];
      }
    };

    this.distanceToolMouseListener = (event) => {
      // Prevent the default map drag behavior.
      event.preventDefault();

      if (event.features[0].properties.itemIndex === 0)
        this.distanceToolSwapDirection();
      this.onMap('mousemove', this.distanceToolOnMove);
      this.map.once('mouseup', this.distanceToolOnMoveEnd);
    };

    this.distanceToolTouchListener = (event) => {
      // Ignore multi touch events
      if (event.points.length !== 1) return;

      // Prevent the default map drag behavior.
      event.preventDefault();

      if (event.features[0].properties.itemIndex === 0)
        this.distanceToolSwapDirection();
      this.onMap('touchmove', this.distanceToolOnMove);
      this.map.once('touchend', this.distanceToolOnMoveEnd);
    };
  }

  async componentDidUpdate(prevProps) {
    const { appViewportSize } = this.props;
    // Exit early if we have yet to initialize the map.
    if (!this.mapInitiated) return;

    if (
      JSON.stringify(prevProps.appViewportSize) !==
      JSON.stringify(appViewportSize)
    ) {
      this.map.resize();
    }

    this.updateFromPropsWhereRequired(prevProps);
  }

  componentWillUnmount() {
    clearTimeout(this.styledataTimeout);
    clearTimeout(this.moveendTimeout);
    this.map.remove();
  }

  setBaseStyle(baseStyle) {
    this.map.setStyle(baseStyle);
    this.mapStarting = false;
  }

  setMarkers(markers, selectedMarkerIds) {
    const features = markers.map((marker) => ({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [+marker.latLon.lon, +marker.latLon.lat],
      },
      properties: {
        ...marker,
        markerSelected:
          selectedMarkerIds && selectedMarkerIds.indexOf(marker.id) !== -1,
      },
    }));
    const source = this.map.getSource('standardMarkerLayer');
    if (source) source.setData({ type: 'FeatureCollection', features });
  }

  setSelectedLayerTypes(selectedLayerTypes) {
    const layerDefinitions = getLayerDefinitions();
    Object.keys(layerDefinitions).forEach((layerType) => {
      const showLayer = selectedLayerTypes.indexOf(layerType) !== -1;
      if (layerDefinitions[layerType]?.onToggle)
        layerDefinitions[layerType].onToggle(this.map, showLayer);
    });
  }

  setHideAllLayers(hidingAllLayers) {
    const { selectedLayerTypes } = this.props;

    const layerDefinitions = getLayerDefinitions();

    Object.keys(layerDefinitions).forEach((layerType) => {
      const layerConfig = layerDefinitions[layerType];

      if (!layerConfig || !layerConfig?.onToggle) {
        return;
      }

      if (hidingAllLayers) {
        layerConfig?.onToggle(this.map, false);
      }

      // Only turn on layers the user has selected
      if (!hidingAllLayers && selectedLayerTypes.includes(layerType)) {
        layerConfig?.onToggle(this.map, true);
      }
    });
  }

  async setDistanceToolVisible(showingDistanceTool) {
    if (showingDistanceTool) {
      if (!this.map.getSource('distanceToolPoints')) {
        this.map.addSource('distanceToolPoints', {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: [],
          },
        });
      }

      if (!this.map.getSource('distanceToolLine')) {
        this.map.addSource('distanceToolLine', {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: [],
          },
        });
      }

      const { lat, lng: lon } = this.map.getCenter();
      const {
        _ne: { lng: right },
        _sw: { lng: left },
      } = wrapBounds(this.map.getBounds());
      const pushOutPercentage = 0.3;
      const difference = ((left - right) / 2) * pushOutPercentage;

      if (!this.pointsData) {
        this.pointsData = {
          type: 'FeatureCollection',
          features: [
            {
              type: 'Feature',
              geometry: {
                type: 'Point',
                coordinates: [lon + difference, lat],
              },
              properties: {
                itemIndex: 0,
              },
            },
            {
              type: 'Feature',
              geometry: {
                type: 'Point',
                coordinates: [lon - difference, lat],
              },
              properties: {
                itemIndex: 1,
              },
            },
          ],
        };
      }

      if (!this.lineData) {
        this.lineData = {
          type: 'FeatureCollection',
          features: [
            {
              type: 'Feature',
              geometry: {
                type: 'LineString',
                coordinates: [
                  [lon + difference, lat],
                  [lon - difference, lat],
                ],
              },
            },
          ],
        };
      }

      this.map.getSource('distanceToolPoints').setData(this.pointsData);
      this.map.getSource('distanceToolLine').setData(this.lineData);

      if (!this.displayToolMarkerElement) {
        this.displayToolMarkerElement = document.createElement('div');
        const distance = calculateDistanceBetweenTwoPointsSphericalLawOfCosines(
          this.pointsData.features[0].geometry.coordinates[1],
          this.pointsData.features[0].geometry.coordinates[0],
          this.pointsData.features[1].geometry.coordinates[1],
          this.pointsData.features[1].geometry.coordinates[0],
          'nm',
        );
        const bearing = calculateBearing(
          this.pointsData.features[0].geometry.coordinates[1],
          this.pointsData.features[0].geometry.coordinates[0],
          this.pointsData.features[1].geometry.coordinates[1],
          this.pointsData.features[1].geometry.coordinates[0],
        ).toFixed(0);

        this.displayToolMarkerElement.innerHTML = `
          <div class="${cx('distanceToolElementContainer')}">
            <div class="${cx(
              'distanceToolElementSpeechBubblePointer',
            )}" style="transform: translateX(${
          156 / 2 - 20 / 2
        }px) translateY(-${20 / 2 + 3}px) rotate(45deg);"></div>
            <div class="${cx(
              'distanceToolElementSpeechBubbleBody',
            )}" style="transform: translateY(-${56 / 2 + 36 + 10}px)">
              <table>
              <tbody>
                <tr>
                  <td class="${cx(
                    'distanceToolElementTitle',
                  )}" style="width: 63px">Distance</td>
                  <td class="${cx(
                    'distanceToolElementTitle',
                  )}" style="width: 63px">Heading</td>
                </tr>
                <tr>
                  <td class="${cx('distanceToolElementInfo')}">
                  <span class="${cx(
                    'distanceToolElementInfoSpan',
                  )}" id="distance">${distance.toFixed(
          distance > 100 ? 0 : 1,
        )}</span>NM
                  </td>
                  <td class="${cx('distanceToolElementInfo')}">
                  <span class="${cx(
                    'distanceToolElementInfoSpan',
                  )}" id="heading">${bearing}</span>°
                  </td>
                </tr>
              </tbody>
              </table>
            </div>
          </div>
        `;
      }

      if (!this.displayToolMarker) {
        this.displayToolMarker = new mapboxgl.Marker({
          element: this.displayToolMarkerElement,
        })
          .setLngLat([lon - difference, lat])
          .addTo(this.map);
      }

      if (!layerExists(this.map, 'distanceToolLine')) {
        this.map.addLayer({
          id: 'distanceToolLine',
          type: 'line',
          source: 'distanceToolLine',
          layout: {
            'line-join': 'round',
            'line-cap': 'round',
          },
        });
      }

      if (!this.map.hasImage('iconPin')) {
        const img = await imgElementSvg(iconPin(), {
          width: 64,
          height: 64,
        });
        img.width = '64';
        img.height = '64';
        this.map.addImage('iconPin', img);
      }

      if (!layerExists(this.map, 'distanceToolPoints')) {
        this.map.addLayer({
          id: 'distanceToolPoints',
          type: 'symbol',
          source: 'distanceToolPoints',
          layout: {
            'icon-image': 'iconPin',
            'icon-size': 0.5,
            'icon-offset': [0, -32],
            'icon-allow-overlap': true,
          },
        });
      }

      if (!this.mouseEventListener) {
        this.mouseEventListener = this.map.on(
          'mousedown',
          'distanceToolPoints',
          this.distanceToolMouseListener,
        );
      }

      if (!this.touchEventListener) {
        this.touchEventListener = this.map.on(
          'touchstart',
          'distanceToolPoints',
          this.distanceToolTouchListener,
        );
      }
    } else {
      if (this.map.getSource('distanceToolPoints')) {
        this.map.getSource('distanceToolPoints').setData({
          type: 'FeatureCollection',
          features: [],
        });
      }
      if (this.map.getSource('distanceToolLine')) {
        this.map.getSource('distanceToolLine').setData({
          type: 'FeatureCollection',
          features: [],
        });
      }
      if (this.displayToolMarker) {
        this.displayToolMarker.remove();
        delete this.displayToolMarker;
      }
      if (this.mouseEventListener) {
        this.map.off(
          'mousedown',
          'distanceToolPoints',
          this.distanceToolMouseListener,
        );
        delete this.mouseEventListener;
      }
      if (this.touchEventListener) {
        this.map.off(
          'touchstart',
          'distanceToolPoints',
          this.distanceToolTouchListener,
        );
        delete this.touchEventListener;
      }
      if (this.pointsData) {
        delete this.pointsData;
      }
      if (this.lineData) {
        delete this.lineData;
      }
    }
  }

  setGeoJsonToDisplay(geoJsonToDisplay) {
    if (!this.map.getSource('geoJsonToDisplay')) {
      this.map.addSource('geoJsonToDisplay', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: [],
        },
      });
    }

    if (!layerExists(this.map, 'geoJsonToDisplay')) {
      this.map.addLayer({
        id: 'geoJsonToDisplay',
        type: 'fill',
        source: 'geoJsonToDisplay',
        layout: {},
        paint: {
          'fill-color': '#005ba6',
          'fill-opacity': 0.5,
        },
      });
    }

    if (geoJsonToDisplay) {
      this.map.getSource('geoJsonToDisplay').setData(geoJsonToDisplay);
    } else {
      this.map.getSource('geoJsonToDisplay').setData({
        type: 'FeatureCollection',
        features: [],
      });
    }
  }

  setIs3dViewEnabled(isEnabled) {
    const {
      dispatchResetBearingAndPitch,
      currentMapParams,
      currentMapboxBearing,
      currentPosition,
    } = this.props;

    if (isEnabled) {
      // 3D Map View is enabled
      this.map.setTerrain({
        source: 'terrain',
        exaggeration: map3dExaggeration,
      });
      this.map.flyTo({
        duration: mapFlyToDuration,
        center: [this.map.getCenter().lng, this.map.getCenter().lat],
        pitch: map3dPitch,
        essential: true, // this animation is considered essential with respect to prefers-reduced-motion
      });

      this.map.touchPitch.enable();
    } else {
      this.map.setTerrain();
      this.map.flyTo({
        duration: mapFlyToDuration,
        center: [this.map.getCenter().lng, this.map.getCenter().lat],
        pitch: 0,
        essential: true, // this animation is considered essential with respect to prefers-reduced-motion
      });
      this.map.touchPitch.disable();
    }
  }

  setLocationMarker(locationMarkerPosition) {
    const currentLocationMarker = document.createElement('div');
    currentLocationMarker.style.width = '48px';
    stringSvg(defaultMapMarker(), {
      width: 48,
      height: 48,
    }).then((innerHTML) => {
      currentLocationMarker.innerHTML = innerHTML;
      // Remove the current weather marker if there is one
      if (this.currentLocationMarker) {
        this.currentLocationMarker.remove();
      }

      if (
        locationMarkerPosition &&
        locationMarkerPosition.lat &&
        locationMarkerPosition.lon
      ) {
        this.currentLocationMarker = new mapboxgl.Marker({
          element: currentLocationMarker,
          anchor: 'center',
        })
          .setLngLat([locationMarkerPosition.lon, locationMarkerPosition.lat])
          .addTo(this.map);
      }
    });
  }

  // This will potentially move somewhere else down the track and be exported for
  // other use cases but is only needed here atm and serves as a POC ;)
  // Reset the popover loading state with the option of resetting the route back to the map
  resetPopoverState(returnToMap = true) {
    const { dispatchShowPopover } = this.props;

    // Reset the route to map (this will also close the sidebar if open)
    if (returnToMap) transitionToNextRoute(ROUTE_MAP);

    // This will reset the popover to the loading state clearing the previous content
    dispatchShowPopover('');
  }

  async updateFromPropsWhereRequired(
    prevProps = {
      selectedMarkerIds: [],
    },
  ) {
    const {
      baseStyle,
      markers,
      selectedMarkerIds,
      selectedLayerTypes,
      hidingAllLayers,
      currentPosition,
      currentHeading,
      currentPositionMarkerHidden,
      tripModeActive,
      followMode,
      showingDistanceTool,
      geoJsonToDisplay,
      showingLocationMapMarker,
      locationMarkerPosition,
      currentMapboxBearing,
      activeTrip,
      activeTripHidden,
      is3dMapEnabled,
    } = this.props;

    const {
      baseStyle: baseStylePrev,
      markers: markersPrev,
      selectedMarkerIds: selectedMarkerIdsPrev,
      selectedLayerTypes: selectedLayerTypesPrev,
      hidingAllLayers: hidingAllLayersPrev,
      currentPosition: currentPositionPrev,
      currentHeading: currentHeadingPrev,
      currentPositionMarkerHidden: currentPositionMarkerHiddenPrev,
      tripModeActive: tripModeActivePrev,
      showingDistanceTool: showingDistanceToolPrev,
      geoJsonToDisplay: geoJsonToDisplayPrev,
      showingLocationMapMarker: showingLocationMapMarkerPrev,
      activeTrip: activeTripPrev,
      activeTripHidden: activeTripHiddenPrev,
      is3dMapEnabled: is3dMapEnabledPrev,
    } = prevProps;

    // For the base style, we set the initial value when instantiating mapbox. When starting up
    // though, our previous value is undefined. In this case, we don't want to call setBaseStyle,
    // as we'll be setting it to the same style we initiated mapbox with, but it will cause a full
    // reload of the base style which we don't want. In other words, we only ever call setBaseStyle
    // if the baseStyle has explicitly changed.
    const previousBaseStyleExists = !!baseStylePrev;
    if (baseStyle !== baseStylePrev && previousBaseStyleExists) {
      this.setBaseStyle(baseStyle);
    }

    // Deep check the map options
    const markersHaveChanged = !deepEqual(markersPrev, markers);
    const selectedMarkerHasChanged = !deepEqual(
      selectedMarkerIdsPrev,
      selectedMarkerIds,
    );

    if ((markersHaveChanged || selectedMarkerHasChanged) && !hidingAllLayers) {
      telemetry(
        telemetry.DEBUG,
        `[mapbox] Updating markers with ${JSON.stringify(markers)}`,
      );
      // Only capture an event when markers have changed
      // We don't want to capture an event when only the selectedMarker has changed
      if (markersHaveChanged) {
        await captureEvent({
          type: 'map: marker impressions',
          payload: {
            markers: markers.map((marker) => ({
              id: marker.id,
              type: marker.markerType,
            })),
          },
        });
      }
      this.setMarkers(markers, selectedMarkerIds);
    }

    if (selectedLayerTypes !== selectedLayerTypesPrev && !hidingAllLayers) {
      this.setSelectedLayerTypes(selectedLayerTypes);
    }

    if (
      hidingAllLayers !== hidingAllLayersPrev &&
      hidingAllLayersPrev !== undefined
    ) {
      this.setHideAllLayers(hidingAllLayers);
    }

    if (showingDistanceTool !== showingDistanceToolPrev) {
      this.setDistanceToolVisible(showingDistanceTool);
    }

    if (geoJsonToDisplay !== geoJsonToDisplayPrev) {
      this.setGeoJsonToDisplay(geoJsonToDisplay);
    }

    if (showingLocationMapMarker !== showingLocationMapMarkerPrev) {
      this.setLocationMarker(locationMarkerPosition);
    }

    if (is3dMapEnabled !== is3dMapEnabledPrev) {
      this.setIs3dViewEnabled(is3dMapEnabled);
    }
  }

  render() {
    const { className, children } = this.props;

    return (
      <div
        className={className}
        ref={(element) => {
          this.mapContainer = element;
        }}
      >
        {children}
      </div>
    );
  }
}

MapBox.propTypes = {
  openSupportOrPresentPaywall: PropTypes.func.isRequired,
  currentMapboxBearing: PropTypes.number.isRequired,
  currentPositionMarkerHidden: PropTypes.bool.isRequired,

  // eslint-disable-next-line react/forbid-prop-types
  history: PropTypes.any.isRequired,
  // activeTrip: PropTypes.bool.isRequired,
  // activeTripHidden: PropTypes.bool.isRequired,
  followMode: PropTypes.string.isRequired,
  followModeActive: PropTypes.bool.isRequired,
  dispatchClearFollowModeActive: PropTypes.func.isRequired,
  dispatchMoveMapToLocation: PropTypes.func.isRequired,
  dispatchResetPitch: PropTypes.func.isRequired,
  dispatchShowPopover: PropTypes.func.isRequired,
  dispatchUpdateSelectedMarker: PropTypes.func.isRequired,
  dispatchSetDropPinLocation: PropTypes.func.isRequired,

  mapControlInitialize: PropTypes.func.isRequired,
  layerDependencies: PropTypes.shape({}).isRequired,
  baseStyle: PropTypes.string.isRequired,
  selectedLayerTypes: PropTypes.arrayOf(PropTypes.string).isRequired,
  hidingAllLayers: PropTypes.bool.isRequired,
  currentPosition: PropTypes.shape({
    lat: PropTypes.number,
    lon: PropTypes.number,
  }).isRequired,
  showingDistanceTool: PropTypes.bool.isRequired,
  onViewPortUpdated: PropTypes.func.isRequired,
  showingLocationMapMarker: PropTypes.bool.isRequired,
  locationMarkerPosition: PropTypes.shape({
    lat: PropTypes.number,
    lon: PropTypes.number,
  }).isRequired,
  currentHeading: PropTypes.number,
  currentMapParams: PropTypes.shape({
    viewPortDetails: PropTypes.shape({
      center: PropTypes.shape({
        lat: PropTypes.number,
        lon: PropTypes.number,
      }),
      bounds: PropTypes.arrayOf(
        PropTypes.shape({
          lat: PropTypes.number,
          lon: PropTypes.number,
        }),
      ),
      zoom: PropTypes.number,
      bearing: PropTypes.number,
    }),
  }),

  markers: PropTypes.arrayOf(PropTypes.shape({})),
  selectedMarkerIds: PropTypes.arrayOf(PropTypes.string),
  geoJsonToDisplay: PropTypes.shape({}),
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.shape({})),
    PropTypes.shape({}),
  ]),
  className: PropTypes.string,

  is3dMapEnabled: PropTypes.bool.isRequired,

  // App viewport size is required to minimize the number of times we call resize.
  appViewportSize: PropTypes.shape({
    width: PropTypes.number,
    height: PropTypes.number,
  }).isRequired,
};

MapBox.defaultProps = {
  currentHeading: null,

  currentMapParams: {
    viewPortDetails: {
      center:
        window.DECKEE_CONFIG && window.DECKEE_CONFIG.estimatedUserLocation
          ? window.DECKEE_CONFIG.estimatedUserLocation
          : { lat: -33.853999, lon: 151.215121 },
      bounds: null,
      zoom: 12,
      bearing: 0,
    },
  },
  is3dMapEnabled: false,
  markers: [],
  selectedMarkerIds: [],
  geoJsonToDisplay: null,
  children: null,
  className: null,
};

// This is a HOC which injects functions from our paywall hook.
// NOTE: We should limit the amount of times the HOC causes renders - this is because of the note below saying not
// to connect to the store. To help prevent issues, we use useMemo to memoize the openSupportOrPresentPaywall function
const withPaywall = (Component) => (props) => {
  const { presentPaywall, hasSubscriptionFeaturesEnabled } = usePaywall(
    'Message support on heatmap',
  );
  const hasActiveSubscription = useSelector(
    selectHasCurrentMemberActiveSubscription,
  );

  const openSupportOrPresentPaywall = useMemo(
    () => () => {
      if (!hasSubscriptionFeaturesEnabled || hasActiveSubscription) {
        transitionToNextRoute(ROUTE_SUPPORT);
      } else {
        presentPaywall();
      }
    },
    [presentPaywall],
  );

  return (
    <Component
      openSupportOrPresentPaywall={openSupportOrPresentPaywall}
      {...props}
    />
  );
};

// NOTE: DON'T connect the MapBox component to a Redux store!
//  >> Instead use mapDispatchToProps/mapStateToProps in the MapContainer and pass in
export default withPaywall(MapBox);
